/// useJobPanel: React hook for job management actions.
/// Provides completeJob and failJob operations against the BPMN engine worker,
/// used by JobDetail and TaskFormRouter components.

import { useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import type { CompleteJobResponse, FailJobResponse } from "@/protocol";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for managing engine jobs: complete or fail an active job.
 * Commands are sent directly to the BPMN engine worker via WorkerManager.
 */
export function useJobPanel() {
  const completeJob = useCallback(async (jobKey: string, variables?: Record<string, unknown>) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) {
      throw new Error("No BPMN engine worker is running");
    }

    const result = await manager.sendCommand(
      engineWorkerId,
      {
        type: "completeJob",
        direction: "cmd" as const,
        payload: {
          job_key: String(jobKey),
          variables: JSON.stringify(variables ?? {}),
        },
      } as never,
      10000,
    );

    const respPayload = result.payload as CompleteJobResponse["payload"];
    if (respPayload.error) {
      throw new Error(respPayload.error);
    }
  }, []);

  const failJob = useCallback(async (jobKey: string, errorMessage: string) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) {
      throw new Error("No BPMN engine worker is running");
    }

    const result = await manager.sendCommand(
      engineWorkerId,
      {
        type: "failJob",
        direction: "cmd" as const,
        payload: {
          job_key: String(jobKey),
          error_message: errorMessage,
        },
      } as never,
      10000,
    );

    const respPayload = result.payload as FailJobResponse["payload"];
    if (respPayload.error) {
      throw new Error(respPayload.error);
    }
  }, []);

  return { completeJob, failJob };
}
